import { t as uiText } from '../i18n';
import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { doc, setDoc } from 'firebase/firestore';
import { db } from '../lib/firebase';
import { useSidebarContext } from '../context/SidebarContext';
import { useHashtagContext } from '../context/HashtagContext';
import { useFolderContext } from '../context/FolderContext';
import { useSearchContext } from '../context/SearchContext';
import { useArchiveContext } from '../context/ArchiveContext';
import { getWorkspaceHashtags } from '../features/hashtag/hashtagUtils';
import { useFolderPageCounts } from '../features/folder/useFolderPageCounts';
import { HashtagData } from '../types/hashtag';
import { FolderData } from '../context/FolderContext';
import FolderTree from './FolderTree';
import './FolderTree/FolderTree.css';
import FolderDeleteConfirmModal from './FolderDeleteConfirmModal';
import addFolderIcon from '../assets/image/icon/add_folder.png';
import { workspacePageListPath } from '../utils/workspaceNavigation';

const PageSidebarContent: React.FC = () => {
  const { closeSidebarIfMobile } = useSidebarContext();
  const { workspaceId } = useParams();
  const navigate = useNavigate();
  const { selectedHashtag, setSelectedHashtag, hashtagRefreshTrigger } = useHashtagContext();
  const { setSearchQuery, setIsSearching } = useSearchContext();
  const { isArchiveMode, setIsArchiveMode } = useArchiveContext();
  const displayWorkspaceId = workspaceId ?? '';
  const [hashtags, setHashtags] = useState<HashtagData[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [newFolderName, setNewFolderName] = useState('');

  const [deleteConfirmModalOpen, setDeleteConfirmModalOpen] = useState(false);
  const [folderToDelete, setFolderToDelete] = useState<FolderData | null>(null);
  const [isDeletingFolder, setIsDeletingFolder] = useState(false);

  const folderInputRef = useRef<HTMLInputElement | null>(null);

  const {
    folders,
    setFolders,
    selectedFolderId,
    setSelectedFolderId,
    openFolderMenuId,
    setOpenFolderMenuId,
    openFolderIds,
    setOpenFolderIds,
    fetchFolders,
    deleteFolder,
    toggleFolderOpen,
  } = useFolderContext();

  const { folderPageCounts, totalPageCount } = useFolderPageCounts(workspaceId);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as HTMLElement;

      if (
        target.classList.contains('folder-menu-button') ||
        target.closest('.folder-menu-button') ||
        target.closest('.folder-menu-dropdown') ||
        target.classList.contains('menu-item') ||
        target.closest('.menu-item')
      ) {
        return;
      }

      if (openFolderMenuId) {
        setOpenFolderMenuId(null);
      }
    };

    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [openFolderMenuId, setOpenFolderMenuId]);

  useEffect(() => {
    if (workspaceId) {
      fetchFolders(workspaceId);
    }
  }, [workspaceId]);

  useEffect(() => {
    const editingFolder = folders.find((folder) => folder.isEditing);
    if (!editingFolder) return;

    let attempts = 0;
    const maxAttempts = 10;

    const tryFocus = () => {
      attempts++;

      const input = document.querySelector<HTMLInputElement>('.folder-name-input');
      if (input) {
        input.focus();
        input.select();
        return;
      }

      console.warn(`❌ [Sidebar] input要素が見つかりませんでした (試行${attempts}/${maxAttempts})`);
      if (attempts < maxAttempts) {
        setTimeout(tryFocus, 100);
      } else {
        console.error('❌ [Sidebar] 最大試行回数に達しました。フォーカスに失敗しました');
      }
    };

    setTimeout(tryFocus, 50);
  }, [folders, openFolderIds]);

  useEffect(() => {
    if (!selectedFolderId) return;

    const selectedFolder = folders.find((folder) => folder.id === selectedFolderId);
    if (!selectedFolder) return;

    const ancestorIds = selectedFolder.ancestorFolderIds || [];
    const missingAncestors = ancestorIds.filter((id) => !openFolderIds.has(id));

    if (missingAncestors.length > 0) {
      setOpenFolderIds((prev) => {
        const newSet = new Set(Array.from(prev));
        missingAncestors.forEach((id) => newSet.add(id));
        return newSet;
      });
    }

    if (selectedFolder.parentId && selectedFolder.parentId !== 'root' && !openFolderIds.has(selectedFolder.parentId)) {
      setOpenFolderIds((prev) => {
        const newSet = new Set(Array.from(prev));
        newSet.add(selectedFolder.parentId!);
        return newSet;
      });
    }
  }, [selectedFolderId, folders, openFolderIds, setOpenFolderIds]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && deleteConfirmModalOpen) {
        setDeleteConfirmModalOpen(false);
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [deleteConfirmModalOpen]);

  useEffect(() => {
    const fetchHashtags = async () => {
      if (!workspaceId) return;

      try {
        setLoading(true);
        const hashtagList = await getWorkspaceHashtags(workspaceId);
        hashtagList.sort((a, b) => b.count - a.count);
        setHashtags(hashtagList);
      } catch (error) {
        console.error('🏷️ [Sidebar] ハッシュタグ一覧の取得に失敗:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchHashtags();
  }, [workspaceId, hashtagRefreshTrigger]);

  const handleAllBoardsClick = () => {
    if (isArchiveMode) {
      setIsArchiveMode(false);
    }

    setSelectedHashtag(null);
    setSelectedFolderId(null);
    setSearchQuery('');
    setIsSearching(false);

    if (!displayWorkspaceId) return;
    navigate(workspacePageListPath(displayWorkspaceId), { replace: true });
    closeSidebarIfMobile();
  };

  const isAllBoardsSelected = !selectedHashtag && !isArchiveMode && !selectedFolderId;

  const handleHashtagClick = (hashtagName: string) => {
    if (isArchiveMode) {
      setIsArchiveMode(false);
    }

    const isSameHashtag = selectedHashtag === hashtagName;
    setSelectedHashtag(isSameHashtag ? null : hashtagName);
    setSelectedFolderId(null);
    setSearchQuery('');
    setIsSearching(false);

    if (workspaceId) {
      navigate(workspacePageListPath(workspaceId));
    }
    closeSidebarIfMobile();
  };

  const handleCreateFolder = async () => {
    if (!workspaceId) return;

    const folderId = `folder_${Date.now()}`;
    const newFolder: FolderData = {
      id: folderId,
      name: '',
      isEditing: true,
      parentId: 'root',
      ancestorFolderIds: null,
      order: folders.filter((folder) => folder.parentId === 'root' || !folder.parentId).length,
    };

    setFolders((prev) => [...prev, newFolder]);
    setNewFolderName('');

    setTimeout(() => {
      folderInputRef.current?.focus();
    }, 0);
  };

  const handleSaveFolderName = async (folderId: string) => {
    if (!workspaceId) return;

    const trimmedName = newFolderName.trim();
    if (!trimmedName) {
      setFolders((prev) => prev.filter((folder) => folder.id !== folderId));
      setNewFolderName('');
      return;
    }

    try {
      setFolders((prev) =>
        prev.map((folder) =>
          folder.id === folderId ? { ...folder, name: trimmedName, isEditing: false } : folder,
        ),
      );
      setNewFolderName('');

      const folder = folders.find((item) => item.id === folderId);
      if (!folder) return;

      const folderRef = doc(db, 'workspaces', workspaceId, 'folders', folderId);
      await setDoc(folderRef, {
        name: trimmedName,
        parentId: folder.parentId || 'root',
        ancestorFolderIds: folder.ancestorFolderIds,
        order: folder.order || 0,
      });
    } catch (error) {
      console.error('❌ [Sidebar] フォルダ名保存エラー:', error);
      setFolders((prev) =>
        prev.map((folder) => (folder.id === folderId ? { ...folder, isEditing: true } : folder)),
      );
      setNewFolderName(trimmedName);
    }
  };

  const handleEditFolderName = (folderId: string, currentName: string) => {
    setFolders((prev) =>
      prev.map((folder) => (folder.id === folderId ? { ...folder, isEditing: true } : folder)),
    );
    setNewFolderName(currentName);

    setTimeout(() => {
      if (folderInputRef.current) {
        folderInputRef.current.focus();
        folderInputRef.current.select();
      }
    }, 0);
  };

  const handleFolderNameKeyPress = (e: React.KeyboardEvent, folderId: string) => {
    if (e.key === 'Enter') {
      handleSaveFolderName(folderId);
    }
  };

  const handleFolderClick = (folderId: string) => {
    if (isArchiveMode) {
      setIsArchiveMode(false);
    }

    const isSameFolder = selectedFolderId === folderId;
    setSelectedHashtag(null);
    setSearchQuery('');
    setIsSearching(false);
    setSelectedFolderId(isSameFolder ? null : folderId);

    if (isSameFolder) {
      if (workspaceId) {
        navigate(workspacePageListPath(workspaceId));
      }
      closeSidebarIfMobile();
      return;
    }

    const selectedFolder = folders.find((folder) => folder.id === folderId);
    if (selectedFolder) {
      const ancestorIds = selectedFolder.ancestorFolderIds || ['root'];

      ancestorIds.forEach((ancestorId) => {
        if (ancestorId !== 'root' && !openFolderIds.has(ancestorId)) {
          setOpenFolderIds((prev) => new Set(Array.from(prev).concat(ancestorId)));
        }
      });

      if (!openFolderIds.has(folderId)) {
        setOpenFolderIds((prev) => new Set(Array.from(prev).concat(folderId)));
      }
    }

    if (workspaceId) {
      navigate(workspacePageListPath(workspaceId));
    }
    closeSidebarIfMobile();
  };

  const handleFolderMenuClick = (e: React.MouseEvent, folderId: string) => {
    e.stopPropagation();

    if (openFolderMenuId === folderId) {
      setOpenFolderMenuId(null);
      return;
    }

    setOpenFolderMenuId(folderId);
  };

  const handleCreateSubFolder = async (parentFolderId: string) => {
    if (!workspaceId) return;

    const folderId = `folder_${Date.now()}`;
    const parentFolder = folders.find((folder) => folder.id === parentFolderId);
    if (!parentFolder) return;

    let ancestorFolderIds: string[] | null;
    if (parentFolderId === 'root') {
      ancestorFolderIds = ['root'];
    } else {
      const parentAncestors = parentFolder.ancestorFolderIds || ['root'];
      ancestorFolderIds = [...parentAncestors, parentFolderId];
    }

    const siblingCount = folders.filter((folder) => folder.parentId === parentFolderId).length;
    const newFolder: FolderData = {
      id: folderId,
      name: '',
      isEditing: true,
      parentId: parentFolderId,
      ancestorFolderIds,
      order: siblingCount,
    };

    setFolders((prev) => [...prev, newFolder]);
    setNewFolderName('');

    setTimeout(() => {
      folderInputRef.current?.focus();
    }, 0);
  };

  const handleFolderMenuAction = (action: string, folderId: string) => {
    switch (action) {
      case 'add':
        setOpenFolderIds((prev) => new Set(Array.from(prev).concat(folderId)));
        setTimeout(() => {
          void handleCreateSubFolder(folderId);
        }, 50);
        setOpenFolderMenuId(null);
        setSelectedFolderId(folderId);
        break;
      case 'rename':
        handleEditFolderName(folderId, folders.find((folder) => folder.id === folderId)?.name || '');
        setOpenFolderMenuId(null);
        setSelectedFolderId(folderId);
        break;
      case 'delete':
        handleDeleteFolder(folderId);
        setOpenFolderMenuId(null);
        break;
      default:
        break;
    }
  };

  const handleDeleteFolder = (folderId: string) => {
    const folder = folders.find((item) => item.id === folderId);
    if (!folder) return;

    setFolderToDelete(folder);
    setDeleteConfirmModalOpen(true);
    setOpenFolderMenuId(null);
  };

  const handleConfirmDeleteFolder = async () => {
    if (!workspaceId || !folderToDelete) return;

    setIsDeletingFolder(true);

    try {
      await deleteFolder(workspaceId, folderToDelete.id);
      setDeleteConfirmModalOpen(false);
      setFolderToDelete(null);
    } catch (error) {
      console.error('❌ [Sidebar] フォルダー削除エラー:', error);
      alert(uiText("Failed to delete folder"));
    } finally {
      setIsDeletingFolder(false);
    }
  };

  const handleCancelDeleteFolder = () => {
    setDeleteConfirmModalOpen(false);
    setFolderToDelete(null);
    setIsDeletingFolder(false);
  };

  const filteredHashtags = hashtags
    .filter((hashtag) => hashtag.count > 0)
    .filter((hashtag) => hashtag.name.toLowerCase().includes(searchTerm.toLowerCase()));

  return (
    <>
      <nav>
        <div style={{ padding: '20px' }}>
          <div style={{ marginBottom: '20px' }}>
            <h3
              style={{
                margin: '0 0 12px 0',
                fontSize: '16px',
                fontWeight: 'bold',
                color: '#333',
              }}
            >
              <button
                onClick={handleAllBoardsClick}
                style={{
                  background: isAllBoardsSelected ? 'var(--workspace-primary, #cfae91)' : 'none',
                  border: 'none',
                  padding: '3px 4px',
                  cursor: 'pointer',
                  textAlign: 'left',
                  width: '100%',
                  fontSize: '16px',
                  fontWeight: 'bold',
                  color: isAllBoardsSelected ? '#fff' : '#333',
                  borderRadius: '4px',
                  transition: 'all 0.2s',
                  marginBottom: '8px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                }}
                onMouseEnter={(e) => {
                  if (!isAllBoardsSelected) {
                    e.currentTarget.style.backgroundColor = 'var(--workspace-primary, #cfae91)';
                    e.currentTarget.style.color = '#fff';
                  }
                }}
                onMouseLeave={(e) => {
                  if (!isAllBoardsSelected) {
                    e.currentTarget.style.backgroundColor = 'transparent';
                    e.currentTarget.style.color = '#333';
                  }
                }}
              >
                <span>{uiText("All pages")}</span>
                <span style={{ fontSize: '11px', color: isAllBoardsSelected ? '#fff' : '#666' }}>
                  {totalPageCount}
                </span>
              </button>
            </h3>

            <div style={{ marginBottom: '12px' }}>
              <h4
                style={{
                  margin: '0 0 12px 0',
                  fontSize: '14px',
                  fontWeight: 'bold',
                  color: '#333',
                }}
              >
                {uiText("Folder")}
              </h4>

              <FolderTree
                folders={folders}
                folderPageCounts={folderPageCounts}
                selectedFolderId={selectedFolderId}
                onFolderSelect={handleFolderClick}
                onFolderMenuClick={handleFolderMenuClick}
                onFolderMenuAction={handleFolderMenuAction}
                openFolderMenuId={openFolderMenuId}
                newFolderName={newFolderName}
                onNameChange={setNewFolderName}
                onSaveName={handleSaveFolderName}
                onKeyPress={handleFolderNameKeyPress}
                inputRef={folderInputRef}
                toggleFolderOpen={toggleFolderOpen}
                openFolderIds={openFolderIds}
                setOpenFolderIds={setOpenFolderIds}
              />

              <button
                onClick={handleCreateFolder}
                style={{
                  background: 'var(--workspace-primary, #cfae91)',
                  border: 'none',
                  padding: '4px 12px',
                  cursor: 'pointer',
                  textAlign: 'center',
                  width: 'auto',
                  fontSize: '13px',
                  fontWeight: 'bold',
                  color: '#fff',
                  borderRadius: '20px',
                  transition: 'all 0.2s',
                  margin: '4px 0px',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '6px',
                  minWidth: 'fit-content',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = 'var(--workspace-primary-dark, #b89a7a)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = 'var(--workspace-primary, #cfae91)';
                }}
              >
                <img src={addFolderIcon} alt={uiText("Add a folder")} style={{ width: '14px', height: '14px' }} />
                {uiText("Create folder")}
              </button>
            </div>
          </div>

          <div style={{ marginBottom: '12px' }}>
            <h4
              style={{
                margin: '0 0 12px 0',
                fontSize: '14px',
                fontWeight: 'bold',
                color: '#333',
              }}
            >
              {uiText("Hashtag")}
            </h4>

            <div style={{ marginBottom: '12px' }}>
              <input
                type="text"
                placeholder={uiText("Search hashtags...")}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                style={{
                  width: '100%',
                  padding: '6px 8px',
                  border: 'none',
                  borderRadius: '4px',
                  fontSize: '12px',
                  boxSizing: 'border-box',
                  backgroundColor: '#fff',
                  outline: 'none',
                }}
                onFocus={(e) => {
                  e.target.style.outline = '2px solid var(--workspace-primary, #cfae91)';
                }}
                onBlur={(e) => {
                  e.target.style.outline = 'none';
                }}
              />
            </div>

            {loading ? (
              <div
                style={{
                  textAlign: 'center',
                  padding: '10px',
                  color: '#666',
                  fontSize: '14px',
                }}
              >
                {uiText("Loading now...")}
              </div>
            ) : filteredHashtags.length === 0 ? (
              <div
                style={{
                  textAlign: 'left',
                  padding: '10px',
                  color: '#666',
                  fontSize: '12px',
                }}
              >
                {searchTerm ? uiText("No results found") : uiText("Hashtags you create are listed here")}
              </div>
            ) : (
              <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                {filteredHashtags.map((hashtag) => (
                  <li key={hashtag.id} style={{ marginBottom: '4px' }}>
                    <button
                      onClick={() => handleHashtagClick(hashtag.name)}
                      style={{
                        background: selectedHashtag === hashtag.name ? 'var(--workspace-primary, #cfae91)' : 'none',
                        border: 'none',
                        padding: '3px 4px',
                        cursor: 'pointer',
                        textAlign: 'left',
                        width: '100%',
                        fontSize: '13px',
                        color: selectedHashtag === hashtag.name ? '#fff' : '#333',
                        borderRadius: '4px',
                        transition: 'all 0.2s',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                      }}
                      onMouseEnter={(e) => {
                        if (selectedHashtag !== hashtag.name) {
                          e.currentTarget.style.backgroundColor = 'var(--workspace-primary, #cfae91)';
                          e.currentTarget.style.color = '#fff';
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (selectedHashtag !== hashtag.name) {
                          e.currentTarget.style.backgroundColor = 'transparent';
                          e.currentTarget.style.color = '#333';
                        }
                      }}
                    >
                      <span style={{ fontWeight: 'bold' }}>#{hashtag.name}</span>
                      <span
                        style={{
                          fontSize: '11px',
                          color: selectedHashtag === hashtag.name ? '#fff' : '#666',
                        }}
                      >
                        {hashtag.count}
                      </span>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      </nav>

      <FolderDeleteConfirmModal
        isOpen={deleteConfirmModalOpen}
        onClose={handleCancelDeleteFolder}
        onConfirm={handleConfirmDeleteFolder}
        folderName={folderToDelete?.name || ''}
        isDeleting={isDeletingFolder}
      />
    </>
  );
};

export default PageSidebarContent;
